import { Context } from "../../context/Contex";
import api from "../../utils/api";
import "./styles.scss";

import { useContext, useEffect, useRef, useState } from "react";

export default function Section() {
    const { selectedType, setSelectedType, history, setHistory, foundGroups, setFoundGroups, stats, setStats, fetchStats} = useContext(Context);
    const [inputValue, setInputValue] = useState('');
    const [status, setStatus] = useState({ message: 'Aguardando leitura...', type: 'idle' });
    
    const [foundFilter, setFoundFilter] = useState('todos');
    
    const inputRef = useRef(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchStats();
    }, []);

    

    const playSound = (type) => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            if (type === 'success') {
                oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
                oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
            } else if (type === 'error') {
                oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
            } else if (type === 'warning') {
                oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
            } else if (type === 'idle') {
                oscillator.frequency.setValueAtTime(500, audioContext.currentTime);
            }

            gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.3);
        } catch (error) {
            console.log('Som não disponível:', error);
        }
    };

    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Tópicos disponíveis: um por responseType que apareceu
    const foundTopics = Object.keys(foundGroups);

    // Total de itens em todos os grupos
    const totalFound = foundTopics.reduce((acc, k) => acc + foundGroups[k].items.length, 0);

    // Grupos filtrados pelo filtro ativo
    const visibleGroups = foundFilter === 'todos'
        ? foundGroups
        : Object.fromEntries(
            Object.entries(foundGroups).filter(([key]) => key === foundFilter)
        );

    // Contagem por tópico para os badges
    const countByTopic = (key) => {
        if (key === 'todos') return totalFound;
        return foundGroups[key]?.items.length || 0;
    };

    const HISTORY_LIMIT = 50;
    const FOUND_LIMIT   = 200;

    const formatCount = (count, limit) =>
        count >= limit ? `${count}+` : String(count);

    // Achados: mostra + se qualquer grupo atingiu o limite
    const anyFoundAtLimit = Object.values(foundGroups).some(g => g.items.length >= FOUND_LIMIT);
    const totalFoundLabel = anyFoundAtLimit ? `${totalFound}+` : String(totalFound);

    const handleScan = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() || loading) return;

        let code = inputValue.trim().toUpperCase();
        const formatRegex = /^[A-Z]{4}[0-9A-Z]{9}TX$/;

        if (!formatRegex.test(code)) {
            setStatus({
                message: `Código inválido: ${code}. Exemplo: TEXAS999999999TX`,
                type: 'error'
            });
            playSound('error');
            setInputValue('');
            setTimeout(() => inputRef.current?.focus(), 100);
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/itens/bipar', {
                awb: code,
                lastRecord: selectedType
            });

            const result = response.data;
            const responseType = result.responseType || 'error';

            if (result.stats) {
                setStats(result.stats);
            }

            let displayMessage = result.message || 'Processado';

            setStatus({
                message: displayMessage,
                type: responseType,
                customColor: result.statusColor
            });

            const newEntry = {
                id: Date.now(),
                code,
                timestamp: new Date().toLocaleTimeString('pt-BR'),
                status: responseType,
                subject: ['idle', 'error', 'warning'].includes(responseType) ? result.message : null,
                isNew: result.isNew,
                isDuplicate: result.isDuplicate,
                shipper: result.data?.shipper,
                route: result.data?.route,
                stage: result.data?.stage,
                returnType: selectedType,
                customColor: result.statusColor
            };

            // Histórico geral
            setHistory(prev => [newEntry, ...prev].slice(0, 50));

            // Achados: tudo que não for 'idle' vai pro grupo do responseType
            if (responseType !== 'idle' && responseType !== 'error' && responseType !== 'warning') {
                setFoundGroups(prev => {
                    const existing = prev[responseType] || {
                        label: displayMessage,  // message vira o título do tópico
                        type: responseType,
                        items: []
                    };
                    return {
                        ...prev,
                        [responseType]: {
                            ...existing,
                            items: [newEntry, ...existing.items].slice(0, 200)
                        }
                    };
                });
            }

            playSound(responseType);

        } catch (error) {
            console.error('Erro ao bipar:', error);

            setStatus({
                message: `Produto ${code} - Erro ao processar`,
                type: 'error'
            });

            const newEntry = {
                id: Date.now(),
                code,
                timestamp: new Date().toLocaleTimeString('pt-BR'),
                status: 'error',
                returnType: selectedType,
            };

            setHistory(prev => [newEntry, ...prev].slice(0, 50));
            playSound('error');
            fetchStats();
        } finally {
            setLoading(false);
            setInputValue('');
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const getCustomStatusStyle = () => {
        if (!status.customColor) return {};
        return {
            background: hexToRgba(status.customColor, 0.05),
            borderColor: hexToRgba(status.customColor, 0.3)
        };
    };

    const getCustomIconStyle = () => {
        if (!status.customColor) return {};
        return { color: status.customColor };
    };

    const getStatusIcon = (statusType) => {
        const icons = {
            entrega: '✓',
            success: '✓',
            found: '✓',
            error: '✗',
            warning: '⚠',
            outros: '⟳',
            devolucao: '↩',
            redespacho: '↗',
            idle: '⟳',
        };
        return icons[statusType] || '•';
    };

    const getHistoryLabel = (item) => {
        const labels = {
            success: 'Encontrado na lista',
            entrega: 'Entrega',
            found: 'Encontrado',
            devolucao: 'Devolução',
            redespacho: 'Redespacho',
            outros: 'Outros',
            idle: 'Não catalogado (já bipado)',
            warning: 'Não catalogado (novo)',
            error: 'Erro ao processar',
        };
        return labels[item.status] || item.status;
    };

    return (
        <div className="dashboard-container">
            <header className="dashboard-header">
                <h1>Bipagem de Produtos</h1>
                <p className="subtitle">Sistema de controle de estoque</p>
            </header>

            {/* Cards de Estatísticas */}
            <div className="stats-grid">
                <div className="stat-card total">
                    <div className="stat-icon">📦</div>
                    <div className="stat-content">
                        <span className="stat-label">Total Escaneado</span>
                        <span className="stat-value">{stats.uniqueCount}</span>
                    </div>
                </div>
                <div className="stat-card success">
                    <div className="stat-icon">✓</div>
                    <div className="stat-content">
                        <span className="stat-label">Encontrados</span>
                        <span className="stat-value">{stats.found}</span>
                    </div>
                </div>
                <div className="stat-card warning">
                    <div className="stat-icon">⚠</div>
                    <div className="stat-content">
                        <span className="stat-label">Não Catalogados</span>
                        <span className="stat-value">{stats.totalNC}</span>
                    </div>
                </div>
                <div className="stat-card error">
                    <div className="stat-icon">✗</div>
                    <div className="stat-content">
                        <span className="stat-label">Erros</span>
                        <span className="stat-value">{stats.notFound}</span>
                    </div>
                </div>
            </div>

            {/* Área de Scan */}
            <div className="scan-area">
                <div className="selectType">
                    <label>Origem do Retorno</label>
                    <div className="select-wrapper">
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            disabled={loading}
                        >
                            <option value="Expedição">Retorno da Expedição</option>
                            <option value="NC">NC ( box D01 - D45 )</option>
                            <option value="Outros">Outros</option>
                        </select>
                    </div>
                </div>

                <div
                    className={`status-display ${status.type}`}
                    style={status.customColor ? getCustomStatusStyle() : {}}
                >
                    <div
                        className="status-icon"
                        style={status.customColor ? getCustomIconStyle() : {}}
                    >
                        {getStatusIcon(status.type)}
                    </div>
                    <p className="status-message">{status.message}</p>
                </div>

                <div className="scan-form">
                    <div className="input-wrapper">
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleScan(e)}
                            placeholder="Escaneie ou digite o código do produto"
                            className="scan-input"
                            disabled={loading}
                        />
                        <button
                            onClick={handleScan}
                            className="scan-button"
                            disabled={loading || !inputValue.trim()}
                        >
                            {loading ? 'Processando...' : 'Registrar'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Painéis lado a lado */}
            <div className="panels-row">

                {/* Histórico Recente */}
                <div className="history-section panel">
                    <h2 className="history-title">
                        Histórico Recente
                        <span className="panel-count">{formatCount(history.length, HISTORY_LIMIT)}</span>
                    </h2>

                    {history.length === 0 ? (
                        <div className="empty-history">
                            <span className="empty-icon">📋</span>
                            <p>Nenhum item escaneado ainda</p>
                        </div>
                    ) : (
                        <div className="history-list scrollable-list">
                            {history.map((item) => (
                                <div key={item.id} className={`history-item ${item.status}`}>
                                    <div className="history-status-icon">
                                        {getStatusIcon(item.status)}
                                    </div>
                                    <div className="history-content">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <span className="history-code">{item.code}</span>
                                            {item.isNew && (
                                                <span className="badge badge-new">NOVO</span>
                                            )}
                                            {item.isDuplicate && (
                                                <span className="badge badge-duplicate">DUPLICADO</span>
                                            )}
                                        </div>
                                        <span className="history-label">{getHistoryLabel(item)}</span>
                                        {item.subject && (
                                            <div className="history-subject">{item.subject}</div>
                                        )}
                                        {item.shipper && (
                                            <div className="history-meta">
                                                🏢 {item.shipper} {item.route && `• 🚚 ${item.route}`}
                                            </div>
                                        )}
                                    </div>
                                    <div className="history-right">
                                        {item.returnType && (
                                            <span className="badge badge-return">{item.returnType}</span>
                                        )}
                                        <span className="history-time">{item.timestamp}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Lista de Achados */}
                <div className="found-section panel">
                    <h2 className="history-title">
                        Achados
                        <span className="panel-count">{totalFoundLabel}</span>
                    </h2>

                    {/* Filtros dinâmicos — um por tópico que apareceu */}
                    {foundTopics.length > 0 && (
                        <div className="found-filters">
                            <button
                                className={`filter-btn ${foundFilter === 'todos' ? 'active' : ''}`}
                                onClick={() => setFoundFilter('todos')}
                            >
                                Todos
                                <span className="filter-count">{totalFoundLabel}</span>
                            </button>
                            {foundTopics.map(key => (
                                <button
                                    key={key}
                                    className={`filter-btn filter-btn--${key} ${foundFilter === key ? 'active' : ''}`}
                                    onClick={() => setFoundFilter(key)}
                                >
                                    {foundGroups[key].label}
                                    <span className="filter-count">{formatCount(countByTopic(key), FOUND_LIMIT)}</span>
                                </button>
                            ))}
                        </div>
                    )}

                    {totalFound === 0 ? (
                        <div className="empty-history">
                            <span className="empty-icon">🔍</span>
                            <p>Nenhum item achado ainda</p>
                        </div>
                    ) : (
                        <div className="scrollable-list">
                            {Object.values(visibleGroups)
                                .flatMap(group => group.items)
                                .sort((a, b) => b.id - a.id)
                                .map((item, idx) => (
                                    <div key={item.id} className={`history-item ${item.status}`}>
                                        <div className="history-status-icon">
                                            {getStatusIcon(item.status)}
                                        </div>
                                        <div className="history-content">
                                            <span className="history-code">{item.code}</span>
                                            {item.subject && (
                                                <div className="history-subject">{item.subject}</div>
                                            )}
                                            {item.shipper && (
                                                <div className="history-meta">
                                                    🏢 {item.shipper}{item.route && ` • 🚚 ${item.route}`}
                                                </div>
                                            )}
                                        </div>
                                        <div className="history-right">
                                            <span className="badge badge-return">{item.returnType}</span>
                                            <span className="history-time">{item.timestamp}</span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}